import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// components
import Header from '../../components/Header/Header';
import HookForm from '../../components/HookForm/HookForm';
import Breadcrumbs from '../../components/Breadcrumbs';
import Loader from '../../components/Loader/Loader';
import SelectClients from '../../components/Selects/SelectClients';
import SelectServiceStatuses from '../../components/Selects/SelectServiceStatuses';
import SelectSources from '../../components/Selects/SelectSources';
import SelectServiceAddress from '../../components/Selects/SelectServiceAddress';
import SelectManagers from '../../components/Selects/SelectManagers';
import Datepicker from '../../components/Inputs/Datepicker/Datepicker';
import Select from '../../components/Select/Select';
import getServiceRecords from '../../api/services/getServiceRecords';
import editServiceRecord from '../../api/services/editServiceRecord';
import alert from '../../helpers/alert';
import { timeOfRecord, datePickerValue, dayString } from '../../utils/recordDate';

// icons
import icon from '../../brand/generated/sidebar/records.svg';

const formStyles = {
  record: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridTemplateAreas: `'a a k k' 'b b b b' 'c c c c' 'd d n n' 'f f f f'`,
  },
  // Ряд под салон старых записей: у новых его нет, и лишняя пустая строка
  // в сетке не нужна.
  legacyDc: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridTemplateAreas: `'a a k k' 'b b b b' 'c c c c' 'd d n n' 'e e e e' 'f f f f'`,
  },
};

const baseTimeOptions = [
  { value: '09:00', label: '09:00' },
  { value: '10:30', label: '10:30' },
  { value: '12:00', label: '12:00' },
  { value: '13:30', label: '13:30' },
  { value: '15:00', label: '15:00' },
  { value: '16:30', label: '16:30' },
];

const RecordService = () => {
  const { id } = useParams();
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editable, setEditable] = useState(false);
  const [record, setRecord] = useState(null);

  const fetchRecord = () =>
    getServiceRecords({ id }).then((res) => setRecord(res?.data?.services?.[0] ?? null));

  useEffect(() => {
    fetchRecord().finally(() => setPageLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (pageLoading) return <Loader />;

  const recordTime = timeOfRecord(record?.date);
  const servicesTitle = (record?.valuesToServices || []).map((item) => item.title).join(', ');
  // Точки тест-драйва и сервисные точки — один справочник, но разные строки:
  // тип записи выбирает, из чего показывать список и фильтровать ли по марке.
  const isTestDrive = record?.type?.id === 1;
  const timeOptions =
    !recordTime || baseTimeOptions.some((o) => o.value === recordTime)
      ? baseTimeOptions
      : [...baseTimeOptions, { value: recordTime, label: recordTime }];

  const editRecord = (data) => {
    setSaving(true);
    const day = dayString(data?.created_at);
    const time = data?.time?.value;
    const payload = {
      id: Number(id),
      typeId: record?.type?.id,
      date: day + ' ' + time + ':00',
      userId: data?.userId?.value,
      statusId: data?.statuse?.value,
      sourceId: data?.sourceId?.value,
      dealerAddressId: data?.dealerAddressId?.value,
      managerId: data?.managerId?.value,
      comment: data?.comment,
    };

    editServiceRecord(payload)
      .then(() => fetchRecord())
      .then(() => setEditable(false))
      .catch((error) => alert.error(error?.response?.data?.errorMessage ?? JSON.stringify(error?.response)))
      .finally(() => setSaving(false));
  };

  // Инпуты держат defaultValue из уже загруженной записи — react-hook-form
  // фиксирует их один раз при монтировании HookForm, поэтому рендерим форму
  // только после того как record гарантированно загружен (см. pageLoading выше)
  const inputs = [
    {
      name: 'userId',
      component: SelectClients,
      label: 'Клиент',
      placeholder: 'Выберите из списка',
      style: { gridArea: 'k' },
      defaultValue: record ? { value: record.id, label: `${record.fio} ${record.phone}`.trim() } : undefined,
      disabled: !editable,
      rules: { required: true },
    },
    {
      name: 'statuse',
      component: SelectServiceStatuses,
      label: 'Статус записи',
      placeholder: 'Выберите из списка',
      defaultValue: record?.status ? { value: record.status.id, label: record.status.title } : undefined,
      disabled: !editable,
      rules: { required: true },
    },
    {
      name: 'sourceId',
      component: SelectSources,
      label: 'Источник',
      placeholder: 'Выберите из списка',
      defaultValue: record?.source ? { value: record.source.id, label: record.source.title } : undefined,
      disabled: !editable,
      rules: { required: true },
    },
    {
      name: 'created_at',
      type: 'date',
      component: Datepicker,
      label: 'Дата',
      defaultValue: datePickerValue(record?.date),
      disabled: !editable,
      rules: { required: true },
    },
    {
      name: 'time',
      component: Select,
      label: 'Время',
      placeholder: 'Выберите из списка',
      options: timeOptions,
      defaultValue: recordTime ? { value: recordTime, label: recordTime } : undefined,
      disabled: !editable,
      rules: { required: true },
    },
    isTestDrive
      ? {
          name: 'car',
          type: 'input',
          label: 'Автомобиль',
          style: { gridArea: 'c' },
          defaultValue: [record?.mark?.name, record?.model?.name].filter(Boolean).join(' '),
          disabled: true,
        }
      : {
          name: 'services',
          type: 'input',
          label: 'Услуга',
          style: { gridArea: 'c' },
          defaultValue: servicesTitle,
          disabled: true,
        },
    {
      name: 'dealerAddressId',
      component: SelectServiceAddress,
      // Для тест-драйва — точки тест-драйва, и только те, где марку записи
      // дают покатать; для сервиса — сервисные, без фильтра по марке.
      typeId: isTestDrive ? 1 : 3,
      markId: isTestDrive ? record?.mark?.id : undefined,
      label: isTestDrive ? 'Адрес тест-драйва' : 'Адрес сервиса',
      placeholder: 'Выберите из списка',
      style: { gridArea: 'n' },
      defaultValue: record?.dealerAddressId
        ? { value: record.dealerAddressId, label: record.dealerAddress }
        : undefined,
      disabled: !editable,
    },
    // Салон записей, созданных до справочника точек тест-драйва. Только
    // показать: выбирать ДЦ больше негде, а терять то, что выбрал менеджер,
    // нельзя.
    ...(record?.dcId
      ? [
          {
            name: 'dcName',
            type: 'input',
            label: 'Салон тест-драйва (старая запись)',
            style: { gridArea: 'e' },
            defaultValue: record?.dcName,
            disabled: true,
          },
        ]
      : []),
    {
      name: 'managerId',
      component: SelectManagers,
      label: 'Ответственный менеджер',
      placeholder: 'Выберите из списка',
      style: { gridArea: 'd' },
      defaultValue: record?.managerId ? { value: record.managerId, label: record.managerFio } : undefined,
      disabled: !editable,
    },
    {
      name: 'comment',
      type: 'input',
      label: 'Заметки/комментарии о записи',
      placeholder: 'Оставьте ваш комментарий',
      style: { gridArea: 'f' },
      defaultValue: record?.comment,
      disabled: !editable,
    },
  ];

  return (
    <div style={{ paddingBottom: '20px' }}>
      {saving && <Loader />}
      <Header title={`Запись ID ${id}`} icon={icon} />
      <Breadcrumbs navigate={'/records'} link={'Записи'} currentPage={`Запись ID ${id}`} />
      <HookForm
        inputs={inputs}
        title={'Информация о записи'}
        grid={record?.dcId ? formStyles.legacyDc : formStyles.record}
        border
        edit={!editable}
        handleFormEdit={() => setEditable(true)}
        action={editable ? 'Сохранить' : undefined}
        onSubmit={editRecord}
      />
    </div>
  );
};

export default RecordService;
